// src/components/titlebar/hooks/useTitleBarMenu.ts

import { useState, useEffect } from "react"
import type { TitleBarMenuState } from "../types/titleBarTypes"

const hasTitleBarMenuStateChanged = (prev: TitleBarMenuState, next: TitleBarMenuState) => {
  const keys = Object.keys(next) as Array<keyof TitleBarMenuState>
  return keys.some(key => prev[key] !== next[key])
}

export const useTitleBarMenu = () => {
  const [state, setState] = useState<TitleBarMenuState>({
    serverMenuAnchor: null,
    serverConnectMenuAnchor: null,
    viewMenuAnchor: null,
    helpMenuAnchor: null,
    currentServer: "서버 미연결",
    currentServerPcName: null,
    discoveredServers: [],
    isDevelopmentMode: false,
    menuData: null,
    searchResultDialogOpen: false,
    searchResults: [],
    isSearching: false,
  })

  // 메뉴 데이터 초기화
  useEffect(() => {
    const initializeMenu = async () => {
      try {
        // localStorage에서 현재 서버 정보 먼저 가져오기
        const savedServer = localStorage.getItem("SigmaServerIp")
        const savedPcName = localStorage.getItem("SigmaServerPcName")

        setState(prev => {
          const next = {
            ...prev,
            currentServer: savedServer || prev.currentServer,
            currentServerPcName: savedPcName || prev.currentServerPcName,
          }
          return hasTitleBarMenuStateChanged(prev, next) ? next : prev
        })

        // menuManager에서 메뉴 데이터 가져오기
        const data = await window.electronAPI?.getMenuData()
        if (data) {
          setState(prev => ({
            ...prev,
            menuData: data,
            currentServer: savedServer || data.serverMenu?.currentServer || prev.currentServer,
            currentServerPcName: savedPcName || data.serverMenu?.currentServerPcName || prev.currentServerPcName,
            discoveredServers: data.serverMenu?.discoveredServers || [],
            isDevelopmentMode: data.viewMenu?.isDevelopmentMode || false,
          }))
        }
      } catch (error) {
        console.error("Failed to load menu data:", error)
        // 폴백: localStorage에서 서버 정보 로드
        const savedServer = localStorage.getItem("SigmaServerIp")
        const savedPcName = localStorage.getItem("SigmaServerPcName")

        setState(prev => {
          const next = {
            ...prev,
            currentServer: savedServer || prev.currentServer,
            currentServerPcName: savedPcName || prev.currentServerPcName,
            isDevelopmentMode: import.meta.env.DEV,
          }
          return hasTitleBarMenuStateChanged(prev, next) ? next : prev
        })
      }
    }

    // 메뉴 업데이트 리스너 등록
    const handleMenuUpdate = (data: any) => {
      // 서버 메뉴에서 현재 서버 정보 추출
      const serverConnectMenu = data.serverMenu?.items?.find((item: any) => item.label === "서버연결")
      const currentServerItem = serverConnectMenu?.submenu?.[0]
      const nextServer = currentServerItem?.label?.includes("기본서버:") ? currentServerItem.label.replace("기본서버: ", "") : null
      const discoveredServers = serverConnectMenu?.submenu?.length > 1 ? serverConnectMenu.submenu.slice(1).map((item: any) => item.label) : []

      setState(prev => {
        const next = {
          ...prev,
          menuData: data,
          currentServer: nextServer || prev.currentServer,
          currentServerPcName: data.serverMenu?.currentServerPcName || prev.currentServerPcName,
          discoveredServers,
          isDevelopmentMode: data.viewMenu?.isDevelopmentMode || false,
        }
        return hasTitleBarMenuStateChanged(prev, next) ? next : prev
      })
    }

    window.electronAPI?.onMenuUpdate?.(handleMenuUpdate)
    initializeMenu()

    return () => {
      window.electronAPI?.removeMenuUpdateListener?.(handleMenuUpdate)
    }
  }, [])

  // localStorage 변경사항 실시간 감지
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "SigmaServerIp") {
        setState(prev => {
          const nextCurrentServer = e.newValue || ""
          if (prev.currentServer === nextCurrentServer) {
            return prev
          }
          return { ...prev, currentServer: nextCurrentServer }
        })
      } else if (e.key === "SigmaServerPcName") {
        setState(prev => {
          const nextCurrentServerPcName = e.newValue || null
          if (prev.currentServerPcName === nextCurrentServerPcName) {
            return prev
          }
          return { ...prev, currentServerPcName: nextCurrentServerPcName }
        })
      }
    }

    const checkLocalStorage = () => {
      const savedServer = localStorage.getItem("SigmaServerIp")
      const savedPcName = localStorage.getItem("SigmaServerPcName")

      setState(prev => {
        const nextCurrentServer = savedServer || prev.currentServer
        const nextCurrentServerPcName = savedPcName || null

        if (prev.currentServer === nextCurrentServer && prev.currentServerPcName === nextCurrentServerPcName) {
          return prev
        }

        return {
          ...prev,
          currentServer: nextCurrentServer,
          currentServerPcName: nextCurrentServerPcName,
        }
      })
    }

    checkLocalStorage()
    const interval = setInterval(checkLocalStorage, 1000)

    window.addEventListener("storage", handleStorageChange)

    return () => {
      window.removeEventListener("storage", handleStorageChange)
      clearInterval(interval)
    }
  }, [])

  return { state, setState }
}
